import { useEffect, useRef } from "react";

type JobUpdateListener = (jobUuid: string) => void;

// Global job notification system
class JobNotificationSystem {
  private listeners = new Map<string, Set<JobUpdateListener>>();
  private globalListeners = new Set<JobUpdateListener>();

  subscribe(jobUuid: string, listener: JobUpdateListener) {
    if (!this.listeners.has(jobUuid)) {
      this.listeners.set(jobUuid, new Set());
    }
    this.listeners.get(jobUuid)!.add(listener);
  }

  unsubscribe(jobUuid: string, listener: JobUpdateListener) {
    const listeners = this.listeners.get(jobUuid);
    if (listeners) {
      listeners.delete(listener);
      if (listeners.size === 0) {
        this.listeners.delete(jobUuid);
      }
    }
  }

  subscribeGlobal(listener: JobUpdateListener) {
    this.globalListeners.add(listener);
  }

  unsubscribeGlobal(listener: JobUpdateListener) {
    this.globalListeners.delete(listener);
  }

  notify(jobUuid: string) {
    // Notify specific job listeners
    const listeners = this.listeners.get(jobUuid);
    if (listeners) {
      listeners.forEach((listener) => listener(jobUuid));
    }

    // Notify global listeners (for counters, etc.)
    this.globalListeners.forEach((listener) => listener(jobUuid));
  }
}

export const jobNotificationSystem = new JobNotificationSystem();

export const useJobNotifications = (jobUuid: string | undefined, callback: JobUpdateListener) => {
  const callbackRef = useRef(callback);
  callbackRef.current = callback;

  useEffect(() => {
    if (!jobUuid) return;

    const listener = (uuid: string) => {
      callbackRef.current(uuid);
    };

    jobNotificationSystem.subscribe(jobUuid, listener);

    return () => {
      jobNotificationSystem.unsubscribe(jobUuid, listener);
    };
  }, [jobUuid]);
};
